import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "motion/react";
import {
  Wand2,
  Hourglass,
  Moon,
  Flame,
  TrendingUp,
  Bell,
  BookOpen,
  Play,
  Pause,
  Check,
  Clock,
  Lock,
  Coins,
  History,
  AlertCircle,
  CircleDot,
} from "lucide-react";
import { Notice } from "@/components/ui";
import { useCompanyId } from "@/lib/CompanyContext";
import {
  routinesApi,
  type Routine,
  type RoutineRunSummary,
  type RoutineStatus,
} from "@/api/routines";

type TabKey = "todas" | "ativas" | "pausadas" | "historico";

/**
 * Página /automacoes — pegada de "alquimia" / "rituais".
 *
 * Cada rotina é um RITUAL: tem ícone próprio, chama-se "ativar" / "pausar"
 * (não "ligar"), tem aura quando ativo (orange glow + shimmer no hover),
 * mostra "quando" → "o que faz" → próxima execução. Nada de criar do zero —
 * só ativar templates existentes.
 */

// ─────────────────────────────────────────────────────────────
// Mapeamento de identidade visual por título da routine
// (quando seed adicionar mais, basta estender o mapa)
// ─────────────────────────────────────────────────────────────

interface RitualIdentity {
  icon: typeof Wand2;
  accent: string; // cor de destaque do ícone
  glow: string; // cor do glow ambient (rgba)
  poetic: string; // tagline curta exibida abaixo do título
  /** Versão amigável da descrição, usada na UI (a description técnica fica no banco e vai pro prompt) */
  userFacingDescription?: string;
}

// Paleta MIV: APENAS navy + orange. Ritual essencial é navy (cor da empresa,
// "fundação"). Outros são orange (acento ND). Diferenciação vem do ícone.
const ND_NAVY = "#063e6e";
const ND_NAVY_GLOW = "rgba(6,62,110,0.32)";
const ND_ORANGE = "#ff7000";
const ND_ORANGE_GLOW = "rgba(255,112,0,0.30)";

const RITUAL_LIBRARY: Record<string, RitualIdentity> = {
  "Aprende com seus aprovados": {
    icon: BookOpen,
    accent: ND_NAVY,
    glow: ND_NAVY_GLOW,
    poetic: "Sua equipe afina o ouvido com você",
    userFacingDescription:
      "Toda madrugada, sua equipe lê os cards que você aprovou e ajustou no dia anterior, identifica o que funcionou e atualiza a memória dos agentes. No dia seguinte, o que cair na sua mesa já chega mais alinhado ao seu gosto.",
  },
  "Relatório semanal de campanhas": {
    icon: TrendingUp,
    accent: ND_ORANGE,
    glow: ND_ORANGE_GLOW,
    poetic: "Sua semana começa com clareza",
    userFacingDescription:
      "Toda segunda às 9h, um resumo das suas campanhas Meta e Google aparece no painel: 3 wins da semana, 3 alertas e 2 ações sugeridas para escalar o que está funcionando.",
  },
  "Lembrete de revisão pendente": {
    icon: Bell,
    accent: ND_ORANGE,
    glow: ND_ORANGE_GLOW,
    poetic: "Sua aprovação destrava resultado",
    userFacingDescription:
      "Quando uma entrega fica esperando aprovação por mais de 2 dias, ela aparece em destaque no painel para você revisar com prioridade. A equipe segue rodando, você só dá o ok.",
  },
  "Relatório semanal de custos": {
    icon: Coins,
    accent: ND_ORANGE,
    glow: ND_ORANGE_GLOW,
    poetic: "Saiba onde está o investimento",
    userFacingDescription:
      "Toda segunda às 8h, um resumo do que sua equipe gastou na semana, em quais agentes e tarefas, com sinais práticos para reduzir custo sem perder qualidade nos entregáveis.",
  },
};

const FALLBACK_RITUAL: RitualIdentity = {
  icon: Wand2,
  accent: ND_ORANGE,
  glow: ND_ORANGE_GLOW,
  poetic: "",
};

function getRitualIdentity(routine: Routine): RitualIdentity {
  return RITUAL_LIBRARY[routine.title] ?? FALLBACK_RITUAL;
}

// ─────────────────────────────────────────────────────────────
// Helpers de tempo
// ─────────────────────────────────────────────────────────────

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) {
    const future = -diffMs;
    if (future < 3600_000) return `em ${Math.round(future / 60_000)}min`;
    if (future < 86_400_000) return `em ${Math.round(future / 3_600_000)}h`;
    return `em ${Math.round(future / 86_400_000)}d`;
  }
  if (diffMs < 60_000) return "agora";
  if (diffMs < 3_600_000) return `há ${Math.round(diffMs / 60_000)}min`;
  if (diffMs < 86_400_000) return `há ${Math.round(diffMs / 3_600_000)}h`;
  return `há ${Math.round(diffMs / 86_400_000)}d`;
}

function describeWhen(routine: Routine): string {
  const t = routine.triggers[0];
  if (!t) return "sem ritmo definido";
  if (t.label) return t.label;
  if (t.kind === "schedule" && t.cronExpression) {
    return cronToHuman(t.cronExpression);
  }
  if (t.kind === "webhook") return "quando algo dispara";
  return "sob demanda";
}

/** Tradução simples de cron → linguagem natural pra UI */
function cronToHuman(cron: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;
  const [min, hour, dom, mon, dow] = parts;

  // Casos comuns
  if (dow === "*" && dom === "*" && mon === "*") {
    if (min !== "*" && hour !== "*") return `todo dia às ${pad(hour)}:${pad(min)}`;
  }
  if (dow !== "*" && min !== "*" && hour !== "*") {
    return `${dowName(dow)} às ${pad(hour)}:${pad(min)}`;
  }
  return cron;
}

function pad(s: string) {
  return s.length === 1 ? `0${s}` : s;
}

function dowName(dow: string): string {
  const map: Record<string, string> = {
    "0": "domingo",
    "1": "segunda",
    "2": "terça",
    "3": "quarta",
    "4": "quinta",
    "5": "sexta",
    "6": "sábado",
    "7": "domingo",
  };
  return map[dow] ?? `dia ${dow}`;
}

// ─────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────

function AutomacoesHero({
  activeCount,
  nextRunIso,
}: {
  activeCount: number;
  nextRunIso: string | null;
}) {
  return (
    <div
      style={{
        position: "relative",
        color: "#fff",
        padding: "44px 32px 36px",
        borderBottom: "1px solid var(--wf-ink-5)",
        overflow: "hidden",
        background: "#050810",
        minHeight: 280,
      }}
    >
      {/* Vídeo alquímico — frasco flutuando + vapor + caustics — image_tail
          garante loop nativo sem cut visível. objectPosition right deixa o
          frasco ancorado à direita (vasto preto à esquerda fica natural pro
          texto) */}
      <video
        src="/banners/banner-rituais.mp4"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "right center",
          zIndex: 0,
        }}
      />
      {/* Vinheta esquerda pra garantir contraste do texto sobre o vídeo,
          mesmo quando 16:9 estica em telas 21:9 mais largas */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(90deg, rgba(5,8,16,0.75) 0%, rgba(5,8,16,0.4) 35%, transparent 60%)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          position: "relative",
          zIndex: 2,
        }}
      >

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: 11,
            fontWeight: 600,
            color: "rgba(255,255,255,0.55)",
            textTransform: "uppercase",
            letterSpacing: "0.10em",
            marginBottom: 10,
          }}
        >
          <Wand2 size={12} strokeWidth={1.75} color="var(--nd-orange)" />
          Automações
        </div>
        <h1
          className="wf-display"
          style={{
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
            margin: 0,
          }}
        >
          Sua equipe trabalhando enquanto você descansa
        </h1>
        <p
          style={{
            fontSize: 13,
            color: "rgba(255,255,255,0.65)",
            marginTop: 8,
            maxWidth: 560,
            fontWeight: 500,
            lineHeight: 1.5,
          }}
        >
          Rituais que rodam sozinhos — todo dia, toda segunda, todo sábado.
          Ative os que fazem sentido pra você e esqueça que existem.
        </p>

        {/* Status pill com glow */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            marginTop: 18,
            padding: "7px 14px",
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 999,
            fontSize: 12,
            color: "rgba(255,255,255,0.85)",
            backdropFilter: "blur(8px)",
            fontWeight: 500,
          }}
        >
          {activeCount > 0 ? (
            <>
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "var(--nd-orange)",
                  boxShadow: "0 0 0 0 rgba(255,112,0,0.6)",
                  animation: "wf-pulse 1.6s infinite",
                }}
              />
              <span>
                {activeCount} {activeCount === 1 ? "ritual ativo" : "rituais ativos"}
              </span>
              {nextRunIso && (
                <>
                  <span style={{ opacity: 0.5 }}>·</span>
                  <span>próximo {formatRelative(nextRunIso)}</span>
                </>
              )}
            </>
          ) : (
            <>
              <Moon size={12} strokeWidth={1.5} />
              <span>nada ativo agora</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Ritual Card
// ─────────────────────────────────────────────────────────────

function RitualCard({
  routine,
  onToggle,
  isPending,
  delay = 0,
}: {
  routine: Routine;
  onToggle: (next: RoutineStatus) => void;
  isPending: boolean;
  delay?: number;
}) {
  const isActive = routine.status === "active";
  const next: RoutineStatus = isActive ? "paused" : "active";
  const ident = getRitualIdentity(routine);
  const Icon = ident.icon;
  const lastRun = routine.lastRun;
  const nextRun = routine.triggers[0]?.nextRunAt;
  const isComingSoon = routine.comingSoon === true;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: isComingSoon ? 0.42 : 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: [0.16, 1, 0.3, 1] }}
      whileHover={isComingSoon ? undefined : { y: -2 }}
      style={{
        position: "relative",
        background: isComingSoon ? "var(--wf-bg)" : "var(--wf-paper)",
        border: `1px solid ${isActive ? ident.accent : "var(--wf-ink-5)"}`,
        borderRadius: 12,
        padding: 20,
        boxShadow: isActive
          ? `0 0 0 4px ${ident.glow}`
          : "0 1px 0 rgba(0,0,0,0.04)",
        opacity: isPending ? 0.6 : 1,
        filter: isComingSoon ? "grayscale(0.6)" : "none",
        transition: "border-color 200ms, box-shadow 200ms",
        overflow: "hidden",
      }}
    >
      {isComingSoon && (
        <span
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 8px",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            background: "var(--wf-ink-6, #f3efe5)",
            color: "var(--wf-ink-2)",
            border: "1px solid var(--wf-ink-5)",
            zIndex: 2,
          }}
        >
          <Lock size={10} strokeWidth={2.5} />
          em breve
        </span>
      )}
      {/* Aurora background quando ativo */}
      {isActive && (
        <motion.div
          aria-hidden
          animate={{ opacity: [0.3, 0.55, 0.3] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: -40,
            right: -40,
            width: 200,
            height: 200,
            background: `radial-gradient(circle, ${ident.glow} 0%, transparent 60%)`,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      )}

      <div style={{ position: "relative", zIndex: 1 }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <motion.div
            animate={
              isActive
                ? { scale: [1, 1.06, 1] }
                : { scale: 1 }
            }
            transition={{
              duration: 2.4,
              repeat: isActive ? Infinity : 0,
              ease: "easeInOut",
            }}
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: isActive
                ? `linear-gradient(135deg, ${ident.accent}22, ${ident.glow})`
                : "var(--wf-ink-5)33",
              color: isActive ? ident.accent : "var(--wf-ink-3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              border: isActive ? `1px solid ${ident.accent}55` : "1px solid transparent",
            }}
          >
            <Icon size={20} strokeWidth={1.75} />
          </motion.div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3
              className="wf-display"
              style={{
                fontSize: 17,
                fontWeight: 700,
                letterSpacing: "-0.015em",
                margin: 0,
                color: "var(--wf-ink)",
                lineHeight: 1.2,
                marginBottom: 2,
              }}
            >
              {routine.title}
            </h3>
            {ident.poetic && (
              <p
                style={{
                  fontSize: 12,
                  color: ident.accent,
                  margin: 0,
                  fontStyle: "italic",
                  opacity: isActive ? 1 : 0.75,
                  fontWeight: 500,
                }}
              >
                {ident.poetic}
              </p>
            )}
          </div>
        </div>

        {/* Description — usa versão amigável da RITUAL_LIBRARY se houver,
            senão a description técnica do banco como fallback */}
        {(ident.userFacingDescription || routine.description) && (
          <p
            style={{
              fontSize: 13,
              color: "var(--wf-ink-2)",
              lineHeight: 1.55,
              margin: 0,
              marginBottom: 14,
            }}
          >
            {ident.userFacingDescription ?? routine.description}
          </p>
        )}

        {/* When → What */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 14,
            fontSize: 12,
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 9px",
              background: "var(--wf-paper)",
              border: "1px solid var(--wf-ink-5)",
              borderRadius: 999,
              fontWeight: 500,
              color: "var(--wf-ink-2)",
            }}
          >
            <Hourglass size={11} strokeWidth={1.75} color="var(--nd-orange)" />
            {describeWhen(routine)}
          </span>
        </div>

        {/* Last/next run footer */}
        {(lastRun || nextRun) && (
          <div
            style={{
              fontSize: 11,
              color: "var(--wf-ink-3)",
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 14,
            }}
          >
            {lastRun && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Check size={10} strokeWidth={2} /> última {formatRelative(lastRun.triggeredAt)}
              </span>
            )}
            {nextRun && isActive && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Clock size={10} strokeWidth={2} /> próxima {formatRelative(nextRun)}
              </span>
            )}
          </div>
        )}

        {/* Toggle button — protegidas mostram badge ao invés de botão */}
        {routine.protected ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 14px",
              borderRadius: 8,
              background: `linear-gradient(135deg, ${ident.accent}10, ${ident.glow})`,
              border: `1px dashed ${ident.accent}55`,
            }}
          >
            <Lock size={13} strokeWidth={1.75} color={ident.accent} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: ident.accent,
                  letterSpacing: "-0.005em",
                }}
              >
                Ritual essencial
              </div>
              <div style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>
                Sempre ativo — não pode ser pausado
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => !isPending && !isComingSoon && onToggle(next)}
            disabled={isPending || isComingSoon}
            className="wf-btn wf-btn--sm"
            style={{
              width: "100%",
              background: isComingSoon
                ? "var(--wf-bg)"
                : isActive
                  ? "var(--wf-paper)"
                  : "var(--nd-orange)",
              color: isComingSoon
                ? "var(--wf-ink-3)"
                : isActive
                  ? "var(--wf-ink)"
                  : "#fff",
              border: isActive || isComingSoon
                ? "1px solid var(--wf-ink-5)"
                : "1px solid #1a1a1a",
              cursor: isComingSoon ? "not-allowed" : isPending ? "wait" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "8px 14px",
            }}
          >
            {isComingSoon ? (
              <>
                <Lock size={12} strokeWidth={2} /> Em breve
              </>
            ) : isPending ? (
              <>processando…</>
            ) : isActive ? (
              <>
                <Pause size={12} strokeWidth={2} /> Pausar ritual
              </>
            ) : (
              <>
                <Play size={12} strokeWidth={2} fill="currentColor" /> Ativar ritual
              </>
            )}
          </button>
        )}
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────
// Histórico de runs
// ─────────────────────────────────────────────────────────────

const RUN_STATUS_META: Record<
  RoutineRunSummary["status"],
  { label: string; color: string; icon: typeof Check }
> = {
  completed: { label: "concluído", color: "#5cb85c", icon: Check },
  // succeeded = run rodou OK sem criar issue. Em rituais que rodam
  // direto-API (Aprende com aprovados), o trabalho real está no
  // triggerPayload (agentsAnalyzed, costCents). O componente
  // HistoryRunRow tem lógica pra customizar o label baseado nesse
  // payload — ver getRunStatusMeta abaixo. Esse aqui é só fallback.
  succeeded: { label: "concluído", color: "#5cb85c", icon: Check },
  running: { label: "rodando", color: "var(--nd-orange)", icon: CircleDot },
  queued: { label: "na fila", color: "var(--wf-ink-3)", icon: Clock },
  failed: { label: "falhou", color: "#ff5577", icon: AlertCircle },
  skipped: { label: "pulado", color: "var(--wf-ink-3)", icon: CircleDot },
  coalesced: { label: "consolidado", color: "var(--wf-ink-3)", icon: CircleDot },
};

/**
 * Custo + extras derivados do triggerPayload pra rituais que rodam
 * direto-API e reportam resumo (ex: "Aprende com aprovados" reporta
 * agentsAnalyzed, agentsSkipped, costCents). Retorna label customizado
 * + linhas de detalhe pra UI mostrar.
 *
 * Quando não há payload rico (run normal), retorna null e o consumidor
 * cai no label genérico do RUN_STATUS_META.
 */
function getRunPayloadSummary(
  run: RoutineRunSummary,
): { label: string; details: string[] } | null {
  const p = run.triggerPayload;
  if (!p || typeof p !== "object") return null;

  const kind = (p as { kind?: string }).kind;
  const agentsAnalyzed = Number((p as { agentsAnalyzed?: number }).agentsAnalyzed ?? 0);
  const agentsSkipped = Number((p as { agentsSkipped?: number }).agentsSkipped ?? 0);
  const costCents = Number((p as { costCents?: number }).costCents ?? 0);
  const durationMs = Number((p as { durationMs?: number }).durationMs ?? 0);

  if (kind === "direct_api_learn_from_approved") {
    if (agentsAnalyzed === 0 && agentsSkipped === 0) {
      return {
        label: "sem entregas pra aprender",
        details: ["Nenhuma tarefa nova foi aprovada ou reprovada na janela analisada."],
      };
    }
    const details: string[] = [];
    if (agentsAnalyzed > 0) {
      details.push(
        `${agentsAnalyzed} ${agentsAnalyzed === 1 ? "agente teve" : "agentes tiveram"} padrões atualizados.`,
      );
    }
    if (agentsSkipped > 0) {
      details.push(
        `${agentsSkipped} ${agentsSkipped === 1 ? "agente foi pulado" : "agentes foram pulados"} (precisam de mais entregas pra extrair padrão).`,
      );
    }
    if (costCents > 0) {
      details.push(`Custo: ${costCents} ND Credits.`);
    }
    if (durationMs > 0) {
      const sec = Math.round(durationMs / 1000);
      details.push(`Duração: ${sec}s.`);
    }
    return {
      label:
        agentsAnalyzed > 0
          ? `${agentsAnalyzed} ${agentsAnalyzed === 1 ? "agente atualizado" : "agentes atualizados"}`
          : "sem entregas pra aprender",
      details,
    };
  }

  return null;
}

function HistoryRunRow({
  run,
  delay,
  onOpenDetail,
}: {
  run: RoutineRunSummary;
  delay: number;
  onOpenDetail: (run: RoutineRunSummary) => void;
}) {
  const ident = RITUAL_LIBRARY[run.routineTitle] ?? FALLBACK_RITUAL;
  const Icon = ident.icon;
  const baseStatusMeta = RUN_STATUS_META[run.status] ?? RUN_STATUS_META.queued;
  const payloadSummary = getRunPayloadSummary(run);
  const statusMeta =
    payloadSummary && run.status === "succeeded"
      ? { ...baseStatusMeta, label: payloadSummary.label }
      : baseStatusMeta;
  const StatusIcon = statusMeta.icon;
  // Click strategy:
  //  - Issue gerada (linkedIssue) → vai pro resultado/execucao da issue
  //  - Sem issue (succeeded/skipped/failed) → abre modal de detalhe explicando
  //    que rodou sem criar tarefa (caso comum em "Aprende com seus aprovados"
  //    quando não há aprovações novas pra processar)
  const issueId = run.linkedIssue?.id;
  const targetPath = issueId
    ? run.linkedIssue?.status === "done"
      ? `/resultado/${issueId}`
      : `/execucao/${issueId}`
    : null;
  const isClickable = !!targetPath || run.status !== "queued";
  const Wrapper = ({ children }: { children: React.ReactNode }) =>
    targetPath ? (
      <Link
        to={targetPath}
        style={{
          textDecoration: "none",
          color: "inherit",
          display: "block",
        }}
      >
        {children}
      </Link>
    ) : isClickable ? (
      <button
        type="button"
        onClick={() => onOpenDetail(run)}
        style={{
          display: "block",
          width: "100%",
          textAlign: "left",
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          color: "inherit",
          font: "inherit",
        }}
      >
        {children}
      </button>
    ) : (
      <>{children}</>
    );
  return (
    <Wrapper>
      <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={targetPath ? { y: -2 } : undefined}
      transition={{ duration: 0.3, delay, ease: [0.16, 1, 0.3, 1] }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "12px 16px",
        background: "var(--wf-paper)",
        border: "1px solid var(--wf-ink-5)",
        borderRadius: 10,
        boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
        cursor: isClickable ? "pointer" : "default",
        transition: "border-color 150ms",
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: `${ident.accent}18`,
          color: ident.accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={15} strokeWidth={1.75} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--wf-ink)",
            letterSpacing: "-0.005em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            marginBottom: 2,
          }}
        >
          {run.routineTitle}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--wf-ink-3)",
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          <span>{formatRelative(run.triggeredAt)}</span>
          {run.linkedIssue && (
            <>
              <span>·</span>
              <span>{run.linkedIssue.title}</span>
            </>
          )}
          {run.failureReason && (
            <>
              <span>·</span>
              <span style={{ color: "#cc3344" }}>{run.failureReason}</span>
            </>
          )}
        </div>
      </div>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontSize: 11,
          fontWeight: 600,
          color: statusMeta.color,
          padding: "3px 9px",
          borderRadius: 999,
          background: `${statusMeta.color}15`,
          flexShrink: 0,
        }}
      >
        <StatusIcon size={11} strokeWidth={2} /> {statusMeta.label}
      </div>
    </motion.div>
    </Wrapper>
  );
}

function HistoryTab({ companyId }: { companyId: string }) {
  const [detailRun, setDetailRun] = useState<RoutineRunSummary | null>(null);
  const runsQuery = useQuery({
    queryKey: ["companies", companyId, "routine-runs"],
    queryFn: () => routinesApi.runs(companyId, 50),
  });

  if (runsQuery.isLoading) {
    return (
      <div className="wf-hand-note" style={{ fontSize: 13 }}>
        carregando histórico…
      </div>
    );
  }
  if (runsQuery.isError) {
    return (
      <div
        className="wf-card"
        style={{
          padding: 14,
          color: "var(--nd-orange-pun)",
          borderColor: "var(--nd-orange)",
        }}
      >
        erro ao carregar histórico: {(runsQuery.error as Error).message}
      </div>
    );
  }
  const runs = runsQuery.data ?? [];
  if (runs.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="wf-card wf-card--dashed"
        style={{
          padding: 32,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 14,
            background: "rgba(255,112,0,0.10)",
            color: "var(--nd-orange)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <History size={20} strokeWidth={1.5} />
        </div>
        <div className="wf-hand-note" style={{ fontSize: 13, color: "var(--wf-ink-2)" }}>
          nenhum ritual rodou ainda — a primeira execução aparece aqui em até 24h
        </div>
      </motion.div>
    );
  }
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {runs.map((run, i) => (
          <HistoryRunRow
            key={run.id}
            run={run}
            delay={i * 0.02}
            onOpenDetail={setDetailRun}
          />
        ))}
      </div>
      <AnimatePresence>
        {detailRun && (
          <RunDetailModal run={detailRun} onClose={() => setDetailRun(null)} />
        )}
      </AnimatePresence>
    </>
  );
}

/**
 * Modal de detalhe de um run sem issue gerada. Explica o que rolou de
 * forma humana, sem jargão técnico — pra o user entender que "rodou e
 * pulou criação" não é falha, é otimização.
 */
function RunDetailModal({
  run,
  onClose,
}: {
  run: RoutineRunSummary;
  onClose: () => void;
}) {
  const ident = RITUAL_LIBRARY[run.routineTitle] ?? FALLBACK_RITUAL;
  const Icon = ident.icon;
  const baseStatusMeta = RUN_STATUS_META[run.status] ?? RUN_STATUS_META.queued;
  const payloadSummary = getRunPayloadSummary(run);
  const statusMeta =
    payloadSummary && run.status === "succeeded"
      ? { ...baseStatusMeta, label: payloadSummary.label }
      : baseStatusMeta;

  // Mensagem humanizada — payload rico (ritual direct-API que reportou
  // trabalho real) tem prioridade sobre a explicação genérica do status.
  const explanation =
    payloadSummary && run.status === "succeeded"
      ? {
          title:
            payloadSummary.label === "sem entregas pra aprender"
              ? "Rodou sem entregas novas pra aprender"
              : "Padrões dos agentes atualizados",
          body:
            payloadSummary.details.join(" ") +
            (payloadSummary.details.length > 0 ? "" : "") +
            ` Esses padrões viram a Camada 3 do prompt — próximas tarefas dos mesmos agentes já saem mais alinhadas com o que você costuma aprovar.`,
        }
      : run.status === "succeeded"
        ? {
            title: "Rodou sem precisar criar tarefa",
            body: `O ritual "${run.routineTitle}" rodou normalmente, mas não encontrou nada novo pra processar nesta execução.`,
          }
        : run.status === "skipped"
          ? {
              title: "Pulado por política",
              body: "Este ritual foi pulado porque já havia uma execução em andamento ou a política de retomada decidiu não rodar agora. Próxima rodada acontece no horário programado.",
            }
          : run.status === "coalesced"
            ? {
                title: "Consolidado em outro run",
                body: "Outro run deste mesmo ritual estava ativo. Pra evitar trabalho duplicado, este run foi consolidado.",
              }
            : run.status === "failed"
              ? {
                  title: "Falha na execução",
                  body: run.failureReason ?? "Erro desconhecido durante execução.",
                }
              : {
                  title: "Detalhes do run",
                  body: "Não há mais informação disponível pra este run.",
                };

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(13,21,38,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ y: 14, scale: 0.97, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        exit={{ y: 14, scale: 0.97, opacity: 0 }}
        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        style={{
          width: "min(480px, 100%)",
          background: "var(--wf-paper)",
          borderRadius: 14,
          border: "1px solid var(--wf-ink-5)",
          padding: 24,
          fontFamily: "var(--wf-font-body)",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: `${ident.accent}18`,
              color: ident.accent,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon size={18} strokeWidth={1.75} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              className="wf-display"
              style={{
                fontSize: 17,
                fontWeight: 700,
                letterSpacing: "-0.01em",
                margin: 0,
                color: "var(--wf-ink)",
              }}
            >
              {run.routineTitle}
            </h2>
            <div
              style={{
                fontSize: 11,
                color: "var(--wf-ink-3)",
                marginTop: 2,
              }}
            >
              {formatRelative(run.triggeredAt)}
            </div>
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontSize: 11,
              fontWeight: 600,
              color: statusMeta.color,
              padding: "3px 9px",
              borderRadius: 999,
              background: `${statusMeta.color}15`,
            }}
          >
            {statusMeta.label}
          </span>
        </div>

        <div
          style={{
            padding: 14,
            background: "var(--wf-bg)",
            border: "1px solid var(--wf-ink-5)",
            borderRadius: 10,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wf-ink)" }}>
            {explanation.title}
          </div>
          <p
            style={{
              fontSize: 12.5,
              color: "var(--wf-ink-2)",
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            {explanation.body}
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="wf-btn wf-btn--primary"
          style={{ alignSelf: "flex-end" }}
        >
          Entendi
        </button>
      </motion.div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────

export default function Automacoes() {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabKey>("todas");

  const routinesQuery = useQuery({
    queryKey: ["companies", companyId, "routines"],
    queryFn: () => routinesApi.list(companyId!),
    enabled: !!companyId,
  });

  const toggle = useMutation({
    mutationFn: ({ routineId, status }: { routineId: string; status: RoutineStatus }) =>
      routinesApi.patch(routineId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies", companyId, "routines"] });
    },
  });

  const all = routinesQuery.data ?? [];

  const counts = useMemo(
    () => ({
      todas: all.length,
      ativas: all.filter((r) => r.status === "active").length,
      pausadas: all.filter((r) => r.status === "paused").length,
    }),
    [all],
  );

  const filtered = useMemo(() => {
    if (tab === "todas") return all;
    if (tab === "ativas") return all.filter((r) => r.status === "active");
    return all.filter((r) => r.status === "paused");
  }, [all, tab]);

  const nextRunIso = useMemo(() => {
    const candidates = all
      .filter((r) => r.status === "active")
      .map((r) => r.triggers[0]?.nextRunAt)
      .filter((x): x is string => !!x)
      .sort();
    return candidates[0] ?? null;
  }, [all]);

  if (!companyId) {
    return (
      <div style={{ padding: 32, color: "var(--wf-ink-2)" }} className="wf-hand-note">
        carregando empresa…
      </div>
    );
  }

  return (
    <div>
      <AutomacoesHero activeCount={counts.ativas} nextRunIso={nextRunIso} />

      {/* Filter tabs */}
      <div className="wf-tabs">
        {(
          [
            ["todas", `Todas (${counts.todas})`],
            ["ativas", `Ativas (${counts.ativas})`],
            ["pausadas", `Pausadas (${counts.pausadas})`],
            ["historico", "Histórico"],
          ] as Array<[TabKey, string]>
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`wf-tab ${tab === key ? "wf-tab--active" : ""}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div style={{ padding: "24px 32px 48px", maxWidth: 1280, margin: "0 auto" }}>
        {tab === "historico" ? (
          <HistoryTab companyId={companyId} />
        ) : (
          <>
        {routinesQuery.isLoading && (
          <div className="wf-hand-note" style={{ fontSize: 13 }}>
            carregando rituais…
          </div>
        )}

        {routinesQuery.isError && (
          <div
            className="wf-card"
            style={{
              padding: 14,
              color: "var(--nd-orange-pun)",
              borderColor: "var(--nd-orange)",
            }}
          >
            erro ao carregar rituais: {(routinesQuery.error as Error).message}
          </div>
        )}

        {routinesQuery.isSuccess && filtered.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="wf-card wf-card--dashed"
            style={{
              padding: 32,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background: "rgba(255,112,0,0.10)",
                color: "var(--nd-orange)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {tab === "ativas" ? (
                <Moon size={20} strokeWidth={1.5} />
              ) : tab === "pausadas" ? (
                <Flame size={20} strokeWidth={1.5} />
              ) : (
                <Wand2 size={20} strokeWidth={1.5} />
              )}
            </div>
            <div className="wf-hand-note" style={{ fontSize: 13, color: "var(--wf-ink-2)" }}>
              {tab === "todas"
                ? "nenhum ritual disponível — rode pnpm seed:nd:routines"
                : tab === "ativas"
                  ? "nada ativo — escolha um ritual em Pausadas pra começar"
                  : "todos os rituais já estão ativos ✨"}
            </div>
          </motion.div>
        )}

        {filtered.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: 16,
            }}
          >
            <AnimatePresence mode="popLayout">
              {filtered.map((routine, i) => (
                <RitualCard
                  key={routine.id}
                  routine={routine}
                  onToggle={(status) => toggle.mutate({ routineId: routine.id, status })}
                  isPending={toggle.isPending && toggle.variables?.routineId === routine.id}
                  delay={i * 0.05}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {toggle.isError && (
          <Notice tone="danger" style={{ marginTop: 12 }}>
            Erro ao alterar status: {(toggle.error as Error).message}
          </Notice>
        )}
          </>
        )}
      </div>
    </div>
  );
}
